import React, { useEffect, useRef, useState } from 'react';
import { Modal, Form, Input, Switch, DatePicker, InputNumber, Button, Space, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import styled from 'styled-components';
import { PopupFormPayload } from './api';
import { MEDIA_URL } from '../../../app/constants/APIKEYS';
import API from '../../../utils/api';

type InternalFormValues = {
  title?: string;
  body?: string;
  imageUrl?: string | null;
  linkUrl?: string;
  linkLabel?: string;
  isActive: boolean;
  showFrom?: Dayjs | null;
  showUntil?: Dayjs | null;
  displayOrder?: number;
};

export type PopupFormModalProps = {
  open: boolean;
  onCancel: () => void;
  onSubmit: (payload: PopupFormPayload) => Promise<void>;
  initialValues?: {
    title?: string;
    body?: string;
    imageUrl?: string | null;
    linkUrl?: string | null;
    linkLabel?: string | null;
    isActive?: boolean | number;
    showFrom?: string | null;
    showUntil?: string | null;
    displayOrder?: number;
  } | null;
  confirmLoading?: boolean;
};

const OPTIONAL_TEXT_PLACEHOLDER = '\u200B'; // zero-width fallback so backend accepts "empty" strings

const stripPlaceholder = (value: string) => value.split(OPTIONAL_TEXT_PLACEHOLDER).join('');

const normalizeInitialText = (value?: string | null) =>
  (typeof value === 'string' ? stripPlaceholder(value).trim() : '');

const prepareOptionalText = (value?: string) => {
  if (typeof value !== 'string') return OPTIONAL_TEXT_PLACEHOLDER;
  const trimmed = stripPlaceholder(value).trim();
  return trimmed.length ? trimmed : OPTIONAL_TEXT_PLACEHOLDER;
};

const PopupFormModal: React.FC<PopupFormModalProps> = ({
  open,
  onCancel,
  onSubmit,
  initialValues,
  confirmLoading,
}) => {
  const [form] = Form.useForm<InternalFormValues>();
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      if (initialValues) {
        form.setFieldsValue({
          title: normalizeInitialText(initialValues.title),
          body: normalizeInitialText(initialValues.body),
          linkUrl: initialValues.linkUrl ?? undefined,
          linkLabel: initialValues.linkLabel ?? undefined,
          isActive: Boolean(initialValues.isActive ?? true),
          showFrom: initialValues.showFrom ? dayjs(initialValues.showFrom) : null,
          showUntil: initialValues.showUntil ? dayjs(initialValues.showUntil) : null,
          displayOrder: initialValues.displayOrder ?? 0,
          imageUrl: initialValues.imageUrl ?? null,
        });
        setPreviewUrl(resolveMediaUrl(initialValues.imageUrl ?? null));
      } else {
        form.resetFields();
        form.setFieldsValue({
          isActive: true,
          displayOrder: 0,
          imageUrl: null,
        });
        setPreviewUrl(null);
      }
    }
  }, [open, initialValues, form]);

  const resolveMediaUrl = (path: string | null | undefined): string | null => {
    if (!path) return null;
    if (/^https?:\/\//i.test(path)) return path;
    return `${MEDIA_URL.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
  };

  const handleFinish = async (values: InternalFormValues) => {
    const formatDate = (value?: Dayjs | null) =>
      value ? value.format('YYYY-MM-DD HH:mm:ss') : null;

    const rawImageUrl = typeof values.imageUrl === 'string' ? values.imageUrl.trim() : '';

    const payload: PopupFormPayload = {
      title: prepareOptionalText(values.title),
      body: prepareOptionalText(values.body),
      imageUrl: rawImageUrl.length ? rawImageUrl : null,
      linkUrl: values.linkUrl?.trim() || null,
      linkLabel: values.linkLabel?.trim() || null,
      isActive: Boolean(values.isActive),
      showFrom: formatDate(values.showFrom),
      showUntil: formatDate(values.showUntil),
      displayOrder: values.displayOrder ?? 0,
    };

    await onSubmit(payload);
  };

  const handleSelectFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      message.error('이미지 파일만 업로드할 수 있습니다.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const response = await API.postImage('POST', '/content?type=popup', '', formData);
      if (response?.message === 'success' && response?.result?.url) {
        const uploadedPath = response.result.url as string;
        form.setFieldsValue({ imageUrl: uploadedPath });
        setPreviewUrl(resolveMediaUrl(uploadedPath));
        message.success('이미지를 업로드했습니다.');
      } else {
        message.error('이미지 업로드에 실패했습니다.');
      }
    } catch (error) {
      console.error(error);
      if ((error as any)?.message === 'IMAGE_SIZE_EXCEEDED') {
        return;
      }
      message.error('이미지 업로드 중 오류가 발생했습니다.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const clearImage = () => {
    form.setFieldsValue({ imageUrl: null });
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Modal
      title={initialValues ? '팝업 수정' : '팝업 등록'}
      open={open}
      onCancel={onCancel}
      okText={initialValues ? '수정' : '등록'}
      cancelText="취소"
      onOk={() => form.submit()}
      confirmLoading={confirmLoading}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
      >
        <Form.Item
          label="제목"
          name="title"
        >
          <Input placeholder="팝업 제목" maxLength={255} />
        </Form.Item>

        <Form.Item
          label="내용"
          name="body"
        >
          <StyledTextArea rows={4} placeholder="팝업 본문" maxLength={4000} />
        </Form.Item>

        <Form.Item
          label="이미지"
          required={false}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space>
              <Button
                onClick={() => fileInputRef.current?.click()}
                loading={uploading}
                disabled={uploading}
              >
                이미지 업로드
              </Button>
              <Button
                danger
                disabled={!form.getFieldValue('imageUrl')}
                onClick={clearImage}
              >
                제거
              </Button>
            </Space>
            <HiddenFileInput
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleSelectFile}
            />
            <Form.Item name="imageUrl" noStyle>
              <Input type="hidden" />
            </Form.Item>
            {previewUrl && (
              <ImagePreview>
                <img src={previewUrl} alt="popup" />
              </ImagePreview>
            )}
          </Space>
        </Form.Item>

        <Form.Item label="링크 URL" name="linkUrl">
          <Input placeholder="https://example.com" />
        </Form.Item>

        <Form.Item label="링크 버튼 라벨" name="linkLabel">
          <Input placeholder="더 알아보기" maxLength={100} />
        </Form.Item>

        <InlineForm>
          <Form.Item label="노출 여부" name="isActive" valuePropName="checked">
            <Switch checkedChildren="노출" unCheckedChildren="숨김" />
          </Form.Item>

          <Form.Item label="정렬 순서" name="displayOrder" initialValue={0}>
            <InputNumber min={0} max={9999} style={{ width: 120 }} />
          </Form.Item>
        </InlineForm>

        <InlineForm>
          <Form.Item label="노출 시작" name="showFrom">
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              style={{ width: '100%' }}
              placeholder="즉시 노출"
            />
          </Form.Item>
          <Form.Item label="노출 종료" name="showUntil">
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              style={{ width: '100%' }}
              placeholder="종료일 없음"
            />
          </Form.Item>
        </InlineForm>
      </Form>
    </Modal>
  );
};

export default PopupFormModal;

const StyledTextArea = styled(Input.TextArea)`
  resize: none;
`;

const InlineForm = styled.div`
  display: flex;
  gap: 16px;

  .ant-form-item {
    flex: 1;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ImagePreview = styled.div`
  width: 100%;
  max-height: 240px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #f0f0f0;
  background: #fafafa;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
`;
